import React, { useEffect, useRef, useState } from 'react';
import {
  isFromToIncomplete,
  patchOnQueryModeChange,
  applyMeasurementFromDateChange,
  getMeasurementQueryMaxDate,
  sanitizeMeasurementToDate,
} from '../../../utils/queryPeriodFilters';
import AppDateInput from '../../../components/Common/AppDateInput/AppDateInput';
import '../MeasurementShell.css';

/**
 * Query Period block for chart pages — matches measurement data page chrome (bp-*).
 * Patient chips / vitals tabs / timer come from MeasurementControlsBar.
 */
const MeasurementChartHeader = ({
  activeVital = 'blood-pressure',
  queryType,
  setQueryType,
  dateRange,
  setDateRange,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  onQuery,
  onReset,
  onPrint,
  onExport,
  onExportPdf,
  onExportCsv,
  onEmail,
  queryDisabled = false,
  resetDisabled = false,
}) => {
  const radioName = `chart-queryType-${String(activeVital || 'bp').replace(/\W+/g, '-')}`;
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (!showExportMenu) return undefined;
    const handleClickOutside = (event) => {
      if (!event.target.closest('.bp-export-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleQueryTypeChange = (nextType) => {
    const patch = patchOnQueryModeChange(nextType);
    setQueryType(patch.queryType);
    if (Object.prototype.hasOwnProperty.call(patch, 'fromDate')) {
      setFromDate(patch.fromDate);
      setToDate(patch.toDate);
    }
  };

  const isQueryBtnDisabled = queryDisabled || isFromToIncomplete(queryType, fromDate, toDate);

  const hasExplicitExportOptions = typeof onExportPdf === 'function' || typeof onExportCsv === 'function';

  return (
    <div className="bp-section bp-section--query" data-export-ignore="true">
      <div className="bp-section-title-row">
        <h2 className="bp-section-title">Query Period</h2>
      </div>
      <div className="bp-card">
        <div className="bp-card-body">
          <div className="bp-query-body">
            <div>
              <label className={`bp-option-card${queryType === 'dateRange' ? ' is-selected' : ''}`}>
                <input
                  type="radio"
                  name={radioName}
                  value="dateRange"
                  checked={queryType === 'dateRange'}
                  onChange={() => handleQueryTypeChange('dateRange')}
                />
                <span className="bp-option-label">Date Range</span>
                <select
                  className="bp-dropdown"
                  value={dateRange}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDateRange(value);
                    if (String(value).toUpperCase() === 'ALL') {
                      setFromDate('');
                      setToDate('');
                    }
                  }}
                  disabled={queryType !== 'dateRange'}
                >
                  <option>Last 30 days</option>
                  <option>Last 7 days</option>
                  <option>Last 14 days</option>
                  <option>Last 60 days</option>
                  <option>Last 90 days</option>
                  <option>All</option>
                </select>
              </label>
              <label className={`bp-option-card${queryType === 'from' ? ' is-selected' : ''}`}>
                <input
                  type="radio"
                  name={radioName}
                  value="from"
                  checked={queryType === 'from'}
                  onChange={() => handleQueryTypeChange('from')}
                />
                <span className="bp-option-label">From / To</span>
                <div className="bp-date-inputs" onClick={(e) => e.stopPropagation()}>
                  <AppDateInput
                    className="bp-date-input"
                    value={fromDate}
                    max={getMeasurementQueryMaxDate()}
                    onChange={(nextFrom) => {
                      const next = applyMeasurementFromDateChange(nextFrom, toDate);
                      setFromDate(next.fromDate);
                      setToDate(next.toDate);
                    }}
                    disabled={queryType !== 'from'}
                  />
                  <span className="bp-date-separator">to</span>
                  <AppDateInput
                    className="bp-date-input"
                    value={toDate}
                    min={fromDate || undefined}
                    max={getMeasurementQueryMaxDate()}
                    onChange={(nextTo) => setToDate(sanitizeMeasurementToDate(nextTo, fromDate))}
                    disabled={queryType !== 'from'}
                  />
                </div>
              </label>
            </div>
            <div>
              <div className="bp-action-buttons-row">
                <button
                  className="bp-action-btn primary"
                  type="button"
                  onClick={onQuery}
                  disabled={isQueryBtnDisabled}
                >
                  Query
                </button>
                {typeof onReset === 'function' && (
                  <button
                    className="bp-action-btn"
                    type="button"
                    onClick={onReset}
                    disabled={resetDisabled}
                  >
                    Reset
                  </button>
                )}
                <button className="bp-action-btn" type="button" onClick={onPrint}>
                  Print
                </button>
                {hasExplicitExportOptions ? (
                  <div className="bp-export-container" ref={exportMenuRef}>
                    <button
                      className="bp-action-btn"
                      type="button"
                      onClick={() => setShowExportMenu((prev) => !prev)}
                      aria-haspopup="menu"
                      aria-expanded={showExportMenu}
                    >
                      Export <span className="bp-export-caret">▾</span>
                    </button>
                    <div className={`bp-export-menu${showExportMenu ? ' show' : ''}`} role="menu">
                      {typeof onExportPdf === 'function' && (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setShowExportMenu(false);
                            onExportPdf();
                          }}
                        >
                          PDF
                        </button>
                      )}
                      {typeof onExportCsv === 'function' && (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setShowExportMenu(false);
                            onExportCsv();
                          }}
                        >
                          CSV
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <button className="bp-action-btn" type="button" onClick={onExport}>
                    Export
                  </button>
                )}
                <button className="bp-action-btn" type="button" onClick={onEmail}>
                  Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeasurementChartHeader;
